import React, { useState } from "react";
import { useQuery } from "react-query";
import {
  Row,
  Col,
  Table,
  Tag,
  Typography,
  Avatar,
  Space,
  Select,
  Button,
  Empty,
  Badge,
  Tooltip,
} from "antd";
import {
  TrophyOutlined,
  FireOutlined,
  StarOutlined,
  CrownOutlined,
  UserOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { getCreditLeaderboard } from "@api/credit/credit";

const { Text, Title } = Typography;
const { Option } = Select;

// Chart card wrapper
const ChartCard = ({ title, subtitle, badge, children, minHeight = 300, extra }) => (
  <div style={{
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid #f1f5f9",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    height: "100%",
  }}>
    <div style={{
      marginBottom: "20px",
      paddingBottom: "16px",
      borderBottom: "1px solid #f1f5f9",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
    }}>
      <div>
        <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", margin: 0 }}>
          {title}
        </h3>
        {subtitle && (
          <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0" }}>
            {subtitle}
          </p>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {extra}
        {badge !== undefined && (
          <Badge count={badge} style={{ backgroundColor: "#8b5cf6" }} overflowCount={999} />
        )}
      </div>
    </div>
    <div style={{ minHeight }}>{children}</div>
  </div>
);

const CreditLeaderboard = () => {
  const [sortBy, setSortBy] = useState("totalCredits");
  const [limit, setLimit] = useState(25);

  const { data: leaderboardData, isLoading, refetch } = useQuery(
    ["creditLeaderboard", sortBy, limit],
    async () => {
      const res = await getCreditLeaderboard(limit, sortBy);
      return res.data?.data || res.data || [];
    },
    { staleTime: 60000 }
  );

  const leaderboard = leaderboardData || [];

  // Rank badge component
  const RankBadge = ({ rank }) => {
    if (rank === 1) {
      return (
        <div style={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 8px rgba(251, 191, 36, 0.4)",
        }}>
          <CrownOutlined style={{ fontSize: "16px", color: "#fff" }} />
        </div>
      );
    }
    if (rank === 2) {
      return (
        <div style={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#fff" }}>2</span>
        </div>
      );
    }
    if (rank === 3) {
      return (
        <div style={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#fff" }}>3</span>
        </div>
      );
    }
    return (
      <div style={{
        width: "32px",
        height: "32px",
        borderRadius: "50%",
        background: "#f1f5f9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>{rank}</span>
      </div>
    );
  };

  // Table columns
  const columns = [
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 80,
      render: (_, record, index) => <RankBadge rank={index + 1} />,
    },
    {
      title: "User",
      key: "user",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Avatar
            size={40}
            src={record.avatar}
            icon={!record.avatar && <UserOutlined />}
            style={{ background: record.avatar ? undefined : "#8b5cf6" }}
          />
          <div>
            <Text strong style={{ fontSize: "14px" }}>
              {record.userName || "Anonymous User"}
            </Text>
            <div>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {record.email}
              </Text>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Total credits earned all-time">
          <span>
            <StarOutlined style={{ marginRight: 4 }} />
            Total Credits
          </span>
        </Tooltip>
      ),
      dataIndex: "totalCredits",
      key: "totalCredits",
      align: "right",
      width: 140,
      sorter: (a, b) => a.totalCredits - b.totalCredits,
      render: (credits) => (
        <Text strong style={{ color: "#10b981", fontSize: "16px" }}>
          {credits.toLocaleString()}
        </Text>
      ),
    },
    {
      title: (
        <Tooltip title="Current consecutive login days">
          <span>
            <FireOutlined style={{ marginRight: 4, color: "#f59e0b" }} />
            Streak
          </span>
        </Tooltip>
      ),
      dataIndex: "streakDays",
      key: "streakDays",
      align: "center",
      width: 100,
      sorter: (a, b) => a.streakDays - b.streakDays,
      render: (streak) => (
        streak > 0 ? (
          <Tag
            color={streak >= 30 ? "gold" : streak >= 7 ? "orange" : "default"}
            style={{ fontWeight: 600 }}
          >
            {streak} days
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: (
        <Tooltip title="Number of achievements unlocked">
          <span>
            <TrophyOutlined style={{ marginRight: 4, color: "#8b5cf6" }} />
            Achievements
          </span>
        </Tooltip>
      ),
      dataIndex: "achievementsCount",
      key: "achievementsCount",
      align: "center",
      width: 120,
      sorter: (a, b) => a.achievementsCount - b.achievementsCount,
      render: (count) => (
        count > 0 ? (
          <Badge
            count={count}
            style={{
              backgroundColor: count >= 10 ? "#8b5cf6" : count >= 5 ? "#3b82f6" : "#94a3b8",
              fontWeight: 600,
            }}
            overflowCount={99}
          />
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: "Last Active",
      dataIndex: "lastActive",
      key: "lastActive",
      width: 120,
      render: (date) => {
        if (!date) return <Text type="secondary">-</Text>;
        const d = new Date(date);
        const now = new Date();
        const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
          return <Tag color="green">Today</Tag>;
        } else if (diffDays === 1) {
          return <Tag color="blue">Yesterday</Tag>;
        } else if (diffDays <= 7) {
          return <Tag color="default">{diffDays}d ago</Tag>;
        } else {
          return <Text type="secondary">{d.toLocaleDateString()}</Text>;
        }
      },
    },
  ];

  // Top 3 podium component
  const TopPodium = () => {
    if (leaderboard.length < 3) return null;

    const top3 = leaderboard.slice(0, 3);
    const [first, second, third] = top3;

    const PodiumCard = ({ user, position, height, color, gradient }) => (
      <div style={{ textAlign: "center" }}>
        <div style={{ marginBottom: "12px" }}>
          <Avatar
            size={position === 1 ? 80 : 64}
            src={user?.avatar}
            icon={!user?.avatar && <UserOutlined />}
            style={{
              background: user?.avatar ? undefined : gradient,
              border: `3px solid ${color}`,
              boxShadow: position === 1 ? `0 4px 20px ${color}40` : undefined,
            }}
          />
          {position === 1 && (
            <div style={{
              position: "relative",
              top: "-20px",
              marginBottom: "-20px",
            }}>
              <CrownOutlined style={{ fontSize: "24px", color: "#fbbf24" }} />
            </div>
          )}
        </div>
        <Text strong style={{ fontSize: position === 1 ? "14px" : "12px", display: "block" }}>
          {user?.userName || "Anonymous"}
        </Text>
        <div style={{
          width: position === 1 ? "100px" : "80px",
          height: `${height}px`,
          background: gradient,
          borderRadius: "8px 8px 0 0",
          margin: "12px auto 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
        }}>
          <div style={{ fontSize: position === 1 ? "24px" : "20px", fontWeight: 700 }}>
            {position}
          </div>
          <div style={{ fontSize: "12px", fontWeight: 600 }}>
            {user?.totalCredits?.toLocaleString() || 0}
          </div>
        </div>
      </div>
    );

    return (
      <div style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: "16px",
        padding: "20px",
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        borderRadius: "12px",
        marginBottom: "20px",
      }}>
        <PodiumCard
          user={second}
          position={2}
          height={80}
          color="#94a3b8"
          gradient="linear-gradient(135deg, #94a3b8 0%, #64748b 100%)"
        />
        <PodiumCard
          user={first}
          position={1}
          height={120}
          color="#fbbf24"
          gradient="linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)"
        />
        <PodiumCard
          user={third}
          position={3}
          height={60}
          color="#d97706"
          gradient="linear-gradient(135deg, #d97706 0%, #b45309 100%)"
        />
      </div>
    );
  };

  return (
    <ChartCard
      title="Credit Leaderboard"
      subtitle="Top users by credits, streaks, and achievements"
      badge={leaderboard.length}
      minHeight={400}
      extra={
        <Space>
          <Select
            value={sortBy}
            onChange={setSortBy}
            style={{ width: 150 }}
            size="small"
          >
            <Option value="totalCredits">By Credits</Option>
            <Option value="streakDays">By Streak</Option>
            <Option value="achievementsCount">By Achievements</Option>
          </Select>
          <Select
            value={limit}
            onChange={setLimit}
            style={{ width: 100 }}
            size="small"
          >
            <Option value={10}>Top 10</Option>
            <Option value={25}>Top 25</Option>
            <Option value={50}>Top 50</Option>
            <Option value={100}>Top 100</Option>
          </Select>
          <Button
            type="text"
            icon={<ReloadOutlined spin={isLoading} />}
            onClick={() => refetch()}
            size="small"
          />
        </Space>
      }
    >
      {leaderboard.length > 0 ? (
        <>
          <TopPodium />
          <Table
            dataSource={leaderboard}
            columns={columns}
            rowKey={(record) => record.userId || record.email}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} users`,
            }}
            loading={isLoading}
            size="small"
            style={{ marginTop: "8px" }}
            rowClassName={(record, index) => {
              if (index < 3) return "top-3-row";
              return "";
            }}
          />
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={isLoading ? "Loading leaderboard..." : "No leaderboard data"}
        />
      )}
    </ChartCard>
  );
};

export default CreditLeaderboard;
